import { PlusIcon, Cross1Icon } from "@radix-ui/react-icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { AxiosError } from "axios";
import { CheckCircleIcon, XCircleIcon } from "lucide-react";
import { useRef, useState } from "react";

import { ResultGrid } from "@/components/CampaignSearchResultGrid";
import { useEvent } from "@/components/hooks/useEvent";
import { MoneyInput } from "@/components/MoneyInput";
import { FeedAvatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input, NumberInput } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { P } from "@/components/ui/typography";
import { SearchResult, validateCoupon } from "@/lib/api";
import { PublicFeed } from "@/lib/types";
import { cn } from "@/lib/utils";

interface AddFeedButtonProps {
  onAddCampaign: (data: {
    feed: PublicFeed;
    cpm_cents: number;
    impressions_budget: number;
    duration: number | undefined;
    reason: string | undefined;
    coupon: string | undefined;
    discount_percentage: number | undefined;
  }) => Promise<void>;
}

export function AddFeedButton({ onAddCampaign }: AddFeedButtonProps) {
  const couponInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [selectedFeed, setSelectedFeed] = useState<SearchResult | null>(null);
  const [search, setSearch] = useState("");
  const [coupon, setCoupon] = useState<string | undefined>(undefined);
  const [cpm, setCpm] = useState<number | undefined>(undefined);
  const [duration, setDuration] = useState<number | undefined>(undefined);
  const [impressions, setImpressions] = useState(10_000);
  const [reason, setReason] = useState("");
  const [couponDirty, setCouponDirty] = useState(false);

  const onOpenChange = useEvent((open: boolean) => {
    if (!open) {
      setSelectedFeed(null);
      setSearch("");
      setCoupon(undefined);
      setCpm(undefined);
      setDuration(undefined);
      setImpressions(10_000);
      setReason("");
      setCouponDirty(false);
    }
    setOpen(open);
  });

  const defaultCpm = (selectedFeed?.algorithm.cpm_cents || 1) / 100;
  const totalAmount = ((cpm ?? defaultCpm) / 1000) * impressions;
  const activeSearch = useDebounce(search, 500);

  const { data: couponResponse } = useQuery({
    queryKey: ["coupon", selectedFeed?.algorithm.id, coupon, totalAmount],
    queryFn: () => {
      if (!coupon) return undefined;
      if (!selectedFeed?.algorithm.id) return undefined;

      return validateCoupon({
        coupon_code: coupon,
        algorithm_id: selectedFeed?.algorithm.id,
        cpm_cents: totalAmount * 100,
      });
    },
  });
  const mutation = useMutation({
    onSuccess: () => {
      onOpenChange(false);
      setSelectedFeed(null);
      setSearch("");
    },
    onError: (error: AxiosError) => console.error(error),
    mutationFn: () => {
      if (!selectedFeed) {
        throw new Error("No feed selected");
      }

      return onAddCampaign({
        feed: selectedFeed.algorithm,
        cpm_cents: (cpm ?? defaultCpm) * 100,
        impressions_budget: impressions,
        reason,
        duration,
        coupon,
        discount_percentage: couponResponse?.valid
          ? couponResponse.discount_percentage
          : undefined,
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <SimpleTooltip message="Add feed to campaign">
        <DialogTrigger asChild>
          <Button size="smIcon" variant="ghost">
            <span className="sr-only">Add feed to campaign</span>
            <PlusIcon className="h-4 w-4" />
          </Button>
        </DialogTrigger>
      </SimpleTooltip>
      <DialogContent>
        <form
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            mutation.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Propose campaign for a feed</DialogTitle>
            <DialogDescription>
              Find a feed to add to this campaign.
            </DialogDescription>
          </DialogHeader>
          <Separator />
          {selectedFeed ? (
            <>
              <div className="flex flex-col gap-6">
                <div
                  className={cn(
                    "p-2",
                    "border border-slate-6 dark:border-slatedark-6 rounded-lg overflow-hidden",
                    "flex flex-col gap-3",
                  )}
                >
                  <div className="flex gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      <FeedAvatar
                        feed={selectedFeed.algorithm}
                        className="max-w-[44px] h-full w-full"
                      />
                      <div className="flex-1 flex flex-col gap-2">
                        <div className="flex flex-col">
                          <div className="text">
                            {selectedFeed.algorithm.display_name}
                          </div>
                          <div className="text-sm text-slate-dim">
                            @{selectedFeed.username}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onPress={() => setSelectedFeed(null)}
                      >
                        <Cross1Icon />
                      </Button>
                    </div>
                  </div>
                </div>

                {selectedFeed.algorithm.algorithm_bio && (
                  <P className="text-slate-dim text-sm whitespace-pre-wrap">
                    {selectedFeed.algorithm.algorithm_bio}
                  </P>
                )}

                <div className="flex flex-col gap-3">
                  <Label htmlFor="budget">CPM</Label>
                  <MoneyInput
                    required
                    name="cpm"
                    placeholder="1.00"
                    id="cpm"
                    min={defaultCpm}
                    value={cpm ?? defaultCpm}
                    onValueChange={setCpm}
                  />
                </div>
                <div className="flex flex-col gap-3">
                  <Label htmlFor="budget">Impressions</Label>
                  <NumberInput
                    isRequired
                    name="impressions"
                    placeholder="100000"
                    id="impressions"
                    value={impressions}
                    onValueChange={setImpressions}
                  />
                </div>
                <div className="flex flex-col gap-3">
                  <Label htmlFor="duration">Duration (in days)</Label>
                  <NumberInput
                    name="duration"
                    id="duration"
                    value={duration}
                    onValueChange={setDuration}
                    min={1}
                    max={100}
                    placeholder="Optionally set a duration for the campaign"
                  />
                </div>

                <div className="flex flex-col gap-3">
                  <Label htmlFor="budget">Message</Label>
                  <Textarea
                    name="reason"
                    placeholder="An optional message to the feed operator"
                    id="reason"
                    value={reason}
                    onValueChange={setReason}
                  />
                </div>

                <div className="flex flex-col">
                  <div className="flex items-start gap-2">
                    <Label className="mb-3" htmlFor="coupon">
                      Coupon Code
                    </Label>
                    {couponResponse?.valid && (
                      <CheckCircleIcon className="h-4 w-4 text-green-10 dark:text-greendark-10" />
                    )}
                    {couponResponse?.valid === false && couponDirty && (
                      <XCircleIcon className="h-4 w-4 text-red-10 dark:text-reddark-10" />
                    )}
                  </div>
                  <Input
                    id="coupon"
                    name="coupon"
                    value={coupon}
                    onBlur={() => setCouponDirty(true)}
                    onValueChange={setCoupon}
                    ref={couponInputRef}
                    color={
                      couponResponse?.valid
                        ? "green"
                        : !couponDirty
                        ? undefined
                        : couponResponse?.valid === false
                        ? "red"
                        : undefined
                    }
                  />

                  {couponResponse?.valid && (
                    <P className="text-sm mt-3 text-green-normal">
                      {couponResponse.message}
                    </P>
                  )}
                  {couponResponse?.valid === false && couponDirty && (
                    <P className="text-sm mt-3 text-red-normal">
                      {couponResponse.error}
                    </P>
                  )}
                </div>
              </div>
              <DialogFooter>
                <div className="flex items-center gap-2 flex-1">
                  <div>Total:</div>
                  <div>
                    {couponResponse?.valid ? (
                      <div className="flex gap-2 items-center">
                        <div className="text-slate-dim relative">
                          $
                          {(couponResponse.amount_saved_cents / 100).toFixed(2)}
                          <span className="absolute top-1/2 inset-x-0 -translate-y-1/2 h-px bg-slate-12 dark:bg-slatedark-12" />
                        </div>
                        <div className="text-green-dim bg-green-6 dark:bg-greendark-6 rounded-lg px-2 py-1 relative">
                          $
                          {(
                            totalAmount -
                            couponResponse.amount_saved_cents / 100
                          ).toFixed(2)}
                        </div>
                      </div>
                    ) : (
                      `$${totalAmount.toFixed(2)}`
                    )}
                  </div>
                </div>
                <Button
                  type="submit"
                  isLoading={mutation.isPending}
                  disabled={!selectedFeed}
                >
                  Propose
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <SearchInput
                value={search}
                onChange={setSearch}
                aria-label="Search for a feed"
              />
              <ResultGrid
                search={activeSearch}
                sort="popularity"
                onSelect={setSelectedFeed}
              />
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
